import Vue from "vue";
import VueRouter from "vue-router";
import { default as VueGtm } from "@gtm-support/vue2-gtm";

const ChamApp = () => import("../views/ChamApp.vue");
const Login = () => import("../views/Login.vue");
const Registration = () => import("../views/Registration.vue");
const SelectEnvironment = () => import("../views/SelectEnvironment.vue");
const SetUpAccount = () => import("../views/SetUpAccount.vue");
const ConfirmRegistrationEmail = () => import("../views/ConfirmRegistrationEmail.vue");
const ConfirmRegistration = () => import("../views/ConfirmRegistration.vue");
const ActivateAccount = () => import("../views/ActivateAccount.vue");
const ConfirmEmailChange = () => import("../views/ConfirmEmailChange.vue");
const ForgotPassword = () => import("../views/ForgotPassword.vue");
const ChangePassword = () => import("../views/ChangePassword.vue");
const SingleSignOn = () => import("../views/SSO.vue");
const AuthSwitchRegistration = () => import("../views/AuthSwitchRegistration.vue");
const AuthSwitchSingleSignOn = () => import("../views/AuthSwitchSSO.vue");

import cookie from "@/utils/cookie";

Vue.use(VueRouter);

const parentWindowUrl = location.hostname;
let cookieEnv = "dev";
let router = "";

if (parentWindowUrl.includes("app.chamaileon.io")) {
	cookieEnv = "prod";
} else if (parentWindowUrl.includes("app-santander.chamaileon.io")) {
	cookieEnv = "santander-prod";
} else if (parentWindowUrl.includes("app-staging.chamaileon.io")) {
	cookieEnv = "staging";
} else if (parentWindowUrl.includes("app-test.chamaileon.io")) {
	cookieEnv = "staging";
}

const userAlreadyLoggedInSafeGuard = (to, from, next) => {
	const chamaileonCookie = cookie.getCookie(`ch-user-access-token-${cookieEnv}`);
	const cognitoCookie = cookie.getCookie(`cognito-token-${cookieEnv}`);

	if (chamaileonCookie && cognitoCookie) {
		return next({ name: "App" });
	}
	return next();
};

const rootPathSafeGuard = (to, from, next) => {
	const chamaileonCookie = cookie.getCookie(`ch-user-access-token-${cookieEnv}`);
	const cognitoCookie = cookie.getCookie(`cognito-token-${cookieEnv}`);
	// userNotLoggedInSafeGuard
	if (!chamaileonCookie || !cognitoCookie) {
		return next({ path: "/login", hash: to.hash, query: to.query });
	}
	return next();
};

const routes = [
	{
		path: "/select-environment",
		name: "SelectEnvironment",
		component: SelectEnvironment,
		meta: {
			title: "Select an environment",
		},
	},
	{
		path: "/login/:idp?",
		name: "Login",
		component: Login,
		beforeEnter: userAlreadyLoggedInSafeGuard,
		meta: {
			title: "Log in to Chamaileon",
			metaTags: [
				{
					name: "description",
					content: "Log in to Chamaileon.io the collaborative email template builder, designer and editor tool",
				},
			],
		},
	},
	{
		path: "/login-as",
		name: "Login as Admin",
		component: Login,
		props: { loginAs: true },
	},
	{
		path: "/register",
		name: "Registration",
		component: Registration,
		beforeEnter: userAlreadyLoggedInSafeGuard,
		meta: {
			title: "Register to Chamaileon",
			metaTags: [
				{
					name: "description",
					content: "Register to Chamaileon.io the collaborative email template builder, designer and editor application",
				},
			],
		},
	},
	{
		path: "/register/confirm-email",
		name: "Confirm Registration Email",
		component: ConfirmRegistrationEmail,
		meta: {
			title: "Confirm Registration Email for Chamaileon",
			metaTags: [
				{
					name: "description",
					content: "Confirm email for Chamaileon.io the collaborative email template builder, designer and editor application",
				},
			],
		},

	},
	{
		path: "/register/account",
		name: "Set Up Account",
		component: SetUpAccount,
		beforeEnter: userAlreadyLoggedInSafeGuard,
		meta: {
			title: "Set Up Chamaileon Account",
			metaTags: [
				{
					name: "description",
					content: "Set an Account on Chamaileon.io the collaborative email template builder, designer and editor application",
				},
			],
		},
	},
	{
		// for confirming email change made from SPA-personal info.
		path: "/confirm-email-change",
		name: "Confirm Email Change",
		component: ConfirmEmailChange,
		meta: {
			title: "Confirm Your Email Address for Chamaileon",
			metaTags: [
				{
					name: "description",
					content: "Confirm email for Chamaileon.io the collaborative email template builder, designer and editor application",
				},
			],
		},

	},
	{
		// this probably not used anymore?
		path: "/confirm-registration",
		name: "Confirm Registration",
		component: ConfirmRegistration,
		meta: {
			title: "Confirm registration for Chamaileon",
			metaTags: [
				{
					name: "description",
					content: "Confirm registration for Chamaileon.io the collaborative email template builder, designer and editor application",
				},
			],
		},

	},
	{
		path: "/account-activation",
		name: "Activate Account",
		component: ActivateAccount,
		meta: {
			title: "Account Activation",
			metaTags: [
				{
					name: "description",
					content: "Account activation for Chamaileon.io the collaborative email template builder, designer and editor application",
				},
			],
		},

	},
	{
		path: "/forgot-password",
		name: "Forgot Password",
		component: ForgotPassword,
		meta: {
			title: "Forgot password for Chamaileon",
			metaTags: [
				{
					name: "description",
					content: "Forgot password for Chamaileon.io the collaborative email template builder, designer and editor application",
				},
			],
		},
	},
	{
		path: "/change-password",
		name: "Change Password",
		component: ChangePassword,
		meta: {
			title: "Change password for Chamaileon",
			metaTags: [
				{
					name: "description",
					content: "Change password for Chamaileon.io the collaborative email template builder, designer and editor application",
				},
			],
		},
	},
	{
		path: "/sso",
		name: "Single sign-on",
		component: SingleSignOn,
	},
	{
		path: "/auth-switch-register",
		name: "Auth Switch - Registration",
		component: AuthSwitchRegistration,
	},
	{
		path: "/auth-switch-sso",
		name: "Auth Switch - Single sign-on",
		component: AuthSwitchSingleSignOn,
	},
	{
		path: "*",
		name: "App",
		component: ChamApp,
		meta: {
			title: "Chamaileon App",
			metaTags: [
				{
					name: "description",
					content: "Chamaileon.io the collaborative email template builder, designer and editor application",
				},
			],
		},
		beforeEnter: rootPathSafeGuard,
	},
];

router = new VueRouter({
	mode: "history",
	routes,
});

Vue.use(VueGtm, {
	id: "GTM-THZSD53",
	enabled: process.env.NODE_ENV !== "development",
	vueRouter: router,
});

export function passStoreToRouter($store) {
	// First we $store the query params from the specified routes
	router.beforeEach((to, from, next) => {
		const filterParamsOn = [
			"/login",
			"/login-as",
			"/register",
			"/register/confirm-email",
			"/register/account",
			"/confirm-email",
			"/confirm-email-change",
			"/confirm-registration",
			"/account-activation",
			"/forgot-password",
			"/change-password",
			"/sso",
			"/auth-switch-register",
			"/auth-switch-sso",
		];
		if (Object.keys(to.query).length > 0 && filterParamsOn.some((pathTemplate) => {
			return to.path.indexOf(pathTemplate) !== -1;
		})) {
			const queryParams = {};
			Object.keys(to.query).forEach((param) => {
				queryParams[param] = to.query[param];
			});
			$store.commit("SET_QUERY_PARAMS", queryParams);
			if ($store.state.environment.name) {
				// Redirect to the path without params if we already have a selected env
				return next({ path: to.path, replace: true });
			}
		}
		return next();
	});

	// After this we redirect to the select-environment path if it's not the current page
	router.beforeEach((to, from, next) => {
		if (to.path.indexOf("/select-environment") !== -1) {
			// we are redirected to the env select route already
			return next();
		}
		if (!$store.state?.environment?.name) {
			$store.commit("environment/SET_REDIRECT_AFTER_ENV_SELECT", to);
			return next({ path: "/select-environment", replace: true });
		}
		if (to.path === "/confirm-email") {
			return next({ path: "/confirm-email-change", replace: true });
		}
		return next();
	});

	// This callback runs before every route change, including on page load.
	router.beforeEach((to, from, next) => {
		// This goes through the matched routes from last to first, finding the closest route with a title.
		// e.g., if we have `/some/deep/nested/route` and `/some`, `/deep`, and `/nested` have titles,
		// `/nested`'s will be chosen.
		const nearestWithTitle = to.matched.slice().reverse().find(r => r.meta && r.meta.title);

		// Find the nearest route element with meta tags.
		const nearestWithMeta = to.matched.slice().reverse().find(r => r.meta && r.meta.metaTags);

		const previousNearestWithMeta = from.matched.slice().reverse().find(r => r.meta && r.meta.metaTags);

		// If a route with a title was found, set the document (page) title to that value.
		if (nearestWithTitle) {
			document.title = nearestWithTitle.meta.title;
		} else if (previousNearestWithMeta) {
			document.title = previousNearestWithMeta.meta.title;
		}

		// Remove any stale meta tags from the document using the key attribute we set below.
		Array.from(document.querySelectorAll("[data-vue-router-controlled]")).map(el => el.parentNode.removeChild(el));

		// Skip rendering meta tags if there are none.
		if (!nearestWithMeta) return next();

		// Turn the meta tag definitions into actual elements in the head.
		nearestWithMeta.meta.metaTags.map((tagDef) => {
			const tag = document.createElement("meta");

			Object.keys(tagDef).forEach((key) => {
				tag.setAttribute(key, tagDef[key]);
			});

			// We use this to track which meta tags we create so we don't interfere with other ones.
			tag.setAttribute("data-vue-router-controlled", "");

			return tag;
		})
			// Add the meta tags to the document head.
			.forEach(tag => document.head.appendChild(tag));

		return next();
	});

	// This callback unsets flag toast on route change
	router.beforeEach((to, from, next) => {
		const flagToasts = {
			"/sso": "ssoRegistrationError",
			"/login": "logoutErrors",
			"/register": "registrationSignUpErrors",
			"/auth-switch-register": "authSwitchFormRegistration",
			"/register/account": "confirmAccountRegistrationError",
		};

		const flagToast = flagToasts[from.path];
		flagToast && $store.commit("flagToast/UNSET", flagToast);

		return next();
	});

	router.afterEach((to) => {
		if ($store.state?.environment?.name !== "prod" && $store.state?.environment?.name !== "prod-santander" && to.name !== "SelectEnvironment") {
			document.title = `${$store.state?.environment?.name || "Select env"} - ${document.title}`;
		}
	});
}

export default router;
